/**
 * Derive a navigation target for a notification from its data payload.
 *
 * Backend notification handlers (apps/notification-service/handlers/*)
 * populate `data` with entity IDs (`leadId`, `taskId`, `dealId`, etc.)
 * but never pre-compute a URL. The frontend already knows the routes,
 * so we derive `actionUrl` here at the consumer boundary. Click in the
 * bell drawer or the toast then navigates to the right detail page.
 *
 * Precedence by specificity — when a notification carries multiple IDs
 * (e.g. a task on a lead), the more-specific entity wins because that
 * is what the notification is *about*. The parent entity is just context.
 *
 * If the backend ever does send an explicit `data.actionUrl`, that wins.
 */
export function deriveActionUrl(
  data: Record<string, unknown> | undefined | null,
): string | undefined {
  if (!data) return undefined;

  const explicit = data['actionUrl'];
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;

  // Order matters: most-specific entity first.
  const candidates: Array<[string, string]> = [
    ['taskId', '/crm/tasks/'],
    ['visitId', '/crm/site-visits/'],
    ['siteVisitId', '/crm/site-visits/'],
    ['caseId', '/crm/cases/'],
    ['quoteId', '/crm/quotes/'],
    ['dealId', '/crm/deals/'],
    ['contactId', '/crm/contacts/'],
    ['accountId', '/crm/accounts/'],
    ['leadId', '/crm/leads/'],
  ];

  for (const [key, prefix] of candidates) {
    const id = data[key];
    if (typeof id === 'string' && id.length > 0) return prefix + id;
  }

  return undefined;
}
